import React, { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch } from 'react-redux';

import { Form } from 'antd';

// Shared UI Library Components
import {
  CustomButton,
  CustomIcon,
  CustomInput,
  CustomTooltip,
  EIconName,
  SwitchElement
} from '@ppmcore/seven-ppm-core-shared-components-react';

// Store entities
import { updateWidgetSettings } from '../../../../../../store/widget-settings/widget-settings.thunks';

// Styles
import './settings-block.scss';

// Models
import { TWidgetType } from '../../../../../../types/widget.types';
import { IWidgetInfo, IWidgetSettings } from '../../../../../../interfaces/widget-settings.interfaces';

export type TSettingsBlockRef = {
  isHasChanges: () => boolean;
  resetState: () => void;
};

type TFormValue = {
  widget_show_text?: boolean;
  widget_text_message?: string;
  widget_title_text: string;
};

type TSettingsBlockProps = {
  type: TWidgetType;
  widget_settings: IWidgetInfo;
  loading?: boolean;
};

export const SettingsBlock = forwardRef<TSettingsBlockRef, TSettingsBlockProps>(
  ({ type, widget_settings, loading = false }: TSettingsBlockProps, ref
  ) => {
    const [form] = Form.useForm<TFormValue>();

    const [hasChanges, setHasChanges] = useState<boolean>(false);

    const dispatch = useDispatch<any>();

    const onValuesChange = (_: { [key in keyof TFormValue]: string | boolean }, allValues: TFormValue): void => {
      const isShowTextChanges = widget_settings.widget_show_text !== allValues.widget_show_text;
      const isTextMessageChanges = widget_settings.widget_text_message !== allValues.widget_text_message;
      const isTitleTextChanges = widget_settings.widget_title_text !== allValues.widget_title_text;

      setHasChanges(isShowTextChanges || isTextMessageChanges || isTitleTextChanges);
    }

    const applyChanges = async (): Promise<void> => {
      const formValue = form.getFieldsValue();

      const { payload } = await dispatch(updateWidgetSettings({
        ...formValue as IWidgetSettings,
        ...(type === 'embedded' && {
          widget_show_text: widget_settings.widget_show_text,
          widget_text_message: widget_settings.widget_text_message,
        })
      }));
      if (!payload) return;

      setHasChanges(false);
    }

    useImperativeHandle(ref, () => ({
      isHasChanges(): boolean {
        return hasChanges;
      },
      resetState(): void {
        form.resetFields();
        setHasChanges(false);
      }
    }));

    return (
      <Form form={ form } onValuesChange={ onValuesChange } className="settings-block">
        <div className="settings-block--header">
          <div className="header-title">
            Settings
          </div>
          <div className="header-action">
            <CustomButton size={ 'small' } disabled={ !hasChanges || loading } onClick={ applyChanges }>
              Apply Changes
            </CustomButton>
          </div>
        </div>
        <div className="settings-block--body">
          {
            type === 'popup' && <>
              <div className="body-item flex-items">
                <div className="body-item-label">
                  Show Text Message
                  <CustomTooltip title={ 'Show Text Message' }>
                    <CustomIcon name={ EIconName.Info } size={ 'size-md' }/>
                  </CustomTooltip>
                </div>
                <Form.Item name="widget_show_text" initialValue={ widget_settings.widget_show_text }>
                  <SwitchElement/>
                </Form.Item>
              </div>
              <div className="body-item">
                <Form.Item name="widget_text_message"
                           initialValue={ widget_settings.widget_text_message }>
                  <CustomInput type={ 'text' } maxLength={ 50 } placeholder={ 'Text Message' }/>
                </Form.Item>
              </div
              >
            </>
          }
          <div className="body-item">
            <div className="body-item-label">
              Title Text:
            </div>
            <Form.Item name="widget_title_text"
                       initialValue={ widget_settings.widget_title_text }>
              <CustomInput type={ 'text' } maxLength={ 35 } placeholder={ 'Title Text' }/>
            </Form.Item>
          </div>
        </div>
      </Form>
    );
  }
)
